import { useState, useRef, Suspense, lazy } from "react";
import { Loader2 } from "lucide-react";
import { Highlight, themes } from "prism-react-renderer";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import { CopyButton } from "~/components/assistant/CopyButton";
import { DetailField } from "~/components/assistant/messages/DetailField";
import { DetailsSection } from "~/components/assistant/messages/DetailsSection";
import { LineageTab } from "~/components/assistant/messages/LineageTab";
import { Figure } from "~/components/assistant/types";
import usePrismLanguages from "~/hooks/usePrismLanguages";
import { trackEvent } from "~/utils/monitoring";

// Lazy load the Plot component
const PlotComponent = lazy(() => import("react-plotly.js"));

interface FigureMessageProps {
  figure: Figure;
  debugMode?: boolean;
}

type TabType = "chart" | "code" | "lineage";

interface Tab {
  id: TabType;
  label: string;
}

// Simple Tabs component for figure display
export function FigureMessage({ figure, debugMode = false }: FigureMessageProps) {
  const [activeTab, setActiveTab] = useState<TabType>("chart");
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const { conversationId, contextId } = useAssistantContext();

  // Load Python language support for syntax highlighting
  const { isLoading, Prism: PrismInstance } = usePrismLanguages(["python"]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);

    trackEvent("analysis_agent_artifact_tab_viewed", {
      dataset_id: contextId || "Unknown",
      conversation_id: conversationId || undefined,
      artifact_id: figure.artifact_id || undefined,
      artifact_type: "figure",
      context: "dataset",
      tab,
    });
  };

  const tabs: Tab[] = [{ id: "chart", label: "Chart" }];
  if (figure.code) {
    tabs.push({ id: "code", label: "Code" });
  }

  if (figure?.artifact_id) {
    tabs.push({ id: "lineage", label: "Lineage" });
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 pt-1 w-full">
      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors select-none cursor-pointer ${
              activeTab === tab.id
                ? "border-gray-900 text-gray-900"
                : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="overflow-x-auto">
        {/* <div> */}
        {activeTab === "chart" && (
          <div className="bg-white rounded-sm">
            <div
              ref={chartContainerRef}
              className="w-full overflow-hidden"
              style={{ height: "450px" }}
            >
              <Suspense
                fallback={
                  <div className="flex items-center justify-center h-96 text-gray-500">
                    <div className="text-center">
                      <Loader2 className="size-8 text-gray-400 animate-spin mx-auto mb-2" />
                      <p className="text-sm">Loading chart...</p>
                    </div>
                  </div>
                }
              >
                <PlotComponent
                  data={figure.content.data}
                  layout={{
                    ...figure.content.layout,
                    // Let Plotly handle dimensions with the container size
                    width: undefined,
                    height: undefined,
                    autosize: true,
                    // Ensure proper margins for responsive layout
                    margin: {
                      l: figure.content.layout.margin?.l || 50,
                      r: figure.content.layout.margin?.r || 50,
                      t: figure.content.layout.margin?.t || 50,
                      b: figure.content.layout.margin?.b || 50,
                    },
                  }}
                  config={{
                    displaylogo: false,
                    responsive: true,
                    displayModeBar: "hover",
                    modeBarButtons: [
                      ["zoomIn2d", "zoomOut2d", "pan2d", "resetScale2d"],
                      ["hoverClosestCartesian", "hoverCompareCartesian"],
                      ["toImage"],
                    ],
                  }}
                  useResizeHandler={true}
                  className="w-full h-full"
                  key={`plot-${figure.artifact_id}`}
                  divId={`plot-${figure.artifact_id}`}
                />
              </Suspense>
            </div>
            {figure?.summary && (
              <div className="mt-3 text-xs text-gray-600 px-2">{figure.summary}</div>
            )}
          </div>
        )}
        {activeTab === "code" && figure.code && (
          <>
            {/* Code Header with Copy Button */}
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-medium text-gray-700">Python Code</h4>
              <CopyButton text={figure.code} variant="light" size="sm" />
            </div>
            <div className="h-full overflow-hidden rounded-sm">
              {!isLoading && (
                <Highlight
                  theme={themes.dracula}
                  code={figure.code}
                  language="python"
                  prism={PrismInstance as any}
                >
                  {({ style, tokens, getLineProps, getTokenProps, className }) => (
                    <pre
                      className={`not-prose h-full p-4 overflow-x-auto text-xs font-mono leading-relaxed ${
                        className || ""
                      }`}
                      style={style}
                    >
                      {tokens.map((line, i) => (
                        <div
                          {...getLineProps({ line, key: i })}
                          key={`line-${i}`}
                          style={{ minHeight: "1.5em" }}
                        >
                          {line.map((token, key) => (
                            <span {...getTokenProps({ token, key })} key={`token-${key}`} />
                          ))}
                        </div>
                      ))}
                    </pre>
                  )}
                </Highlight>
              )}
              {isLoading && (
                <div className="h-full p-4 bg-gray-800 text-gray-300 text-sm font-mono leading-relaxed">
                  Loading syntax highlighting...
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === "lineage" && figure?.artifact_id && (
          <LineageTab artifactId={figure.artifact_id} />
        )}
      </div>

      {/* Debug Section */}
      {debugMode && (
        <div className="mt-2">
          <DetailsSection
            content={
              <div className="space-y-3 pt-2 pb-4 px-4 rounded-lg border text-gray-800 border-gray-200">
                <DetailField label="Figure Data" content={figure} />
              </div>
            }
            defaultExpanded={false}
          />
        </div>
      )}
    </div>
  );
}
